import { XMarkIcon } from '@heroicons/react/20/solid';
import {
  cn,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader
} from '@heroui/react';
import { useMemo } from 'react';

import { motion } from 'framer-motion';
import MarkdownContent from 'src/features/product/product-detail/ui/updates-tab/markdown-content/MarkdownContent';
import { CTA_RFQ_SUPPORT } from 'src/shared/constants/update';
import { RFQ } from 'src/shared/types/product';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import { isImageMimeType } from 'src/shared/utils/fileTypeUtils';

import { ButtonV2, Typography } from 'src/shared/components/ui';

import useProductUpdateInputModal from '../../hooks/useProductUpdateInputModal';

/** Extracts the source value from a file's tags (e.g. 'source:user' -> 'user') */
const getFileSource = (tags: string[]): string => {
  const sourceTag = tags.find((tag) => tag.startsWith('source:'));

  return sourceTag ? sourceTag.replace('source:', '') : 'other';
};

const SOURCE_LABELS: Record<string, string> = {
  user: 'Uploaded Files',
  reference: 'Reference Images',
  ai: 'AI Generated',
  rfq_extracted: 'Extracted from Document'
};

const SOURCE_ORDER = ['user', 'reference', 'ai', 'rfq_extracted'];

interface ProductRfqDrawerProps {
  rfq?: RFQ | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const ProductRfqDrawer: React.FC<ProductRfqDrawerProps> = ({
  rfq,
  isOpen,
  onOpenChange
}) => {
  const { setData: setUpdateInputData } = useProductUpdateInputModal();

  const filesBySource = useMemo(() => {
    const files: UploadedFile[] = rfq?.files ?? [];
    const grouped: Record<string, UploadedFile[]> = {};

    for (const file of files) {
      if (file.tags.includes('visibility:hidden')) continue;

      const source = getFileSource(file.tags);

      if (!grouped[source]) {
        grouped[source] = [];
      }
      grouped[source].push(file);
    }

    return grouped;
  }, [rfq?.files]);

  const sortedSourceKeys = useMemo(() => {
    const keys = Object.keys(filesBySource);

    return keys.sort((a, b) => {
      const indexA = SOURCE_ORDER.indexOf(a);
      const indexB = SOURCE_ORDER.indexOf(b);
      const orderA = indexA === -1 ? SOURCE_ORDER.length : indexA;
      const orderB = indexB === -1 ? SOURCE_ORDER.length : indexB;

      return orderA - orderB;
    });
  }, [filesBySource]);

  const hasFiles = sortedSourceKeys.length > 0;

  // const handleDownloadPDF = () => {
  //   // Navigate to print page
  //   const rfqId = rfq?.id;
  //   if (rfqId) {
  //     window.open(`/build-anything/print?rfq=${rfqId}`, '_blank');
  //   }
  // };

  return (
    <Drawer
      hideCloseButton
      isOpen={isOpen}
      placement="right"
      size="3xl"
      onOpenChange={onOpenChange}
    >
      <DrawerContent>
        {(onClose) => (
          <>
            <DrawerHeader className="flex justify-between items-center border-b border-gray-4">
              <Typography variant="h3" weight="bold" className="ml-3">
                {rfq?.document?.name || 'RFQ Document'}
              </Typography>
              <div className="flex items-center gap-2">
                <ButtonV2
                  onPress={() => {
                    setUpdateInputData(
                      { name: rfq?.document?.name ?? '' },
                      CTA_RFQ_SUPPORT
                    );
                  }}
                  size="md"
                  color="secondary"
                >
                  Request support
                </ButtonV2>
                {/* <ButtonV2
                  onPress={handleDownloadPDF}
                  size="md"
                  color="tertiary"
                  startContent={
                    <ArrowDownTrayIcon className="size-4 pointer-events-none" />
                  }
                >
                  Download PDF
                </ButtonV2> */}
                <ButtonV2
                  size="md"
                  onPress={() => onClose()}
                  isIconOnly
                  color="tertiary"
                >
                  <XMarkIcon className="size-4" />
                </ButtonV2>
              </div>
            </DrawerHeader>
            <DrawerBody
              className="px-10 pt-4 pb-10"
              style={{
                backgroundImage: `
          linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
        `,
                backgroundSize: '40px 40px',
                backgroundColor: '#FFF'
              }}
            >
              <div>
                {rfq?.rfq_markdown && (
                  <motion.div
                    className={cn('prose prose-slate z-[999999] relative')}
                    initial={{ opacity: 1 }}
                    animate={{
                      opacity: 1,
                      transition: { duration: 1, ease: 'easeInOut', delay: 2.5 }
                    }}
                  >
                    <MarkdownContent markdownString={rfq?.rfq_markdown} />
                  </motion.div>
                )}
              </div>
              {hasFiles && (
                <div className="space-y-6">
                  {sortedSourceKeys.map((source) => (
                    <div key={source}>
                      <Typography
                        variant="body-1"
                        weight="bold"
                        className="mb-2"
                      >
                        {SOURCE_LABELS[source] ?? source}
                      </Typography>

                      <div className="flex flex-col gap-2">
                        {filesBySource[source].map((file) => {
                          const isImage = isImageMimeType(
                            file.content_type ?? ''
                          );

                          return (
                            <div
                              key={file.id}
                              className="flex items-center gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white p-2"
                            >
                              {isImage ? (
                                <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-50">
                                  <img
                                    src={file.url}
                                    alt={file.description || file.name}
                                    className="h-full w-full object-cover"
                                    loading="lazy"
                                  />
                                </div>
                              ) : null}
                              <p className="min-w-0 truncate text-sm text-gray-700">
                                {file.description || file.name}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </DrawerBody>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default ProductRfqDrawer;
